/**
 * PALO back-office design tokens — extracted verbatim from Christian's redesign
 * prototype (#2132). Single source of truth for the back-office look. The shop
 * stays on theme-next and is not affected by anything here.
 */
export const tokens = {
    color: {
        // Ground & chrome
        canvas: '#F4F6F8',
        surface: '#FFFFFF',
        surfaceAlt: '#FAFBFC',
        /** One step below surfaceAlt — the hover of a row in a dense list. */
        surface2: '#F7F9FB',
        chrome: '#1B2027',
        chrome2: '#242B34',
        chromeLine: '#333C47',
        // Text
        ink: '#1A2027',
        ink2: '#5C6670',
        ink3: '#8A939C',
        inkInvert: '#FFFFFF',
        // Accent (brand)
        accent: '#1565D8',
        accentHi: '#0F4FAD',
        accentSoft: '#E8F1FD',
        accentLine: '#B9D4F7',
        // Semantics (kept separate from the accent)
        ok: '#2E7D32',
        okSoft: '#E7F4E8',
        okInk: '#1F6323',
        warn: '#E8850F',
        warnSoft: '#FDF1E0',
        warnInk: '#A85C08',
        crit: '#D5004A',
        critSoft: '#FDE7EE',
        neutral: '#6F7377',
        neutralSoft: '#EEEFF1',
        neutralInk: '#5A6068',
        // The internal remark has a colour of its own in the prototype: it is
        // neither a state nor a warning, and must not read as either.
        internal: '#6B3FA0',
        internalSoft: '#F0EAF8',
        internalLine: '#C9B3E3',
        // Kinds of document. Status already takes grey, blue, amber, red and
        // green, so the kind of a document stays clear of all five.
        kindInquiry: '#3F3A9E',
        kindInquirySoft: '#EDECFA',
        kindOrder: '#0E6A6E',
        kindOrderSoft: '#E1F2F2',
        kindInvoice: '#7A5A14',
        kindInvoiceSoft: '#F6EEDB',
        kindPriceInfo: '#7E3A68',
        kindPriceInfoSoft: '#F5E8F1',
        kindBonus: '#4F5C17',
        kindBonusSoft: '#EDF1DC',
        // Lines
        line: '#E3E6EA',
        lineSoft: '#EEF1F4',
        lineHard: '#CFD5DC',
        // Tinted borders, for the badges and dots that sit on a soft ground
        okLine: '#A9D6AC',
        warnLine: '#F3CE9A',
        critLine: '#F3B0C6',
    },
    size: {
        /**
         * A block control — a drop zone, a remark, an editor — is this tall.
         *
         * They sit next to each other in the two-column form rows, and each one
         * used to bring its own height: a drop zone 144 from the screen design,
         * a two-row remark 86. The row then read as broken rather than as two
         * fields.
         */
        blockH: 144,
        rowH: 38,
        rowHHead: 34,
        railW: 56,
        navW: 212,
        topH: 48,
    },
    radius: {
        default: 6,
        small: 4,
    },
    shadow: {
        s1: '0 1px 2px rgba(26,32,39,.08), 0 1px 3px rgba(26,32,39,.04)',
        s2: '0 4px 12px rgba(26,32,39,.10), 0 1px 3px rgba(26,32,39,.06)',
        s3: '0 16px 48px rgba(26,32,39,.20), 0 4px 12px rgba(26,32,39,.10)',
    },
    font: {
        sans: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif',
        mono: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, monospace',
    },
} as const;

export type PaloTokens = typeof tokens;
